import { Flex } from 'antd';
import React from 'react';
import { AiPanelBlock, AiPanelBlockType } from '@tools/shared';
import CopyToClipboard from '@tools/ui/components/common/CopyToClipboard';
import {
  Divider,
  IconSize,
  IconType,
  IconWrap,
  semanticTheme,
  Typography
} from '@tools/ui/dime';
import { theme } from '@tools/ui-utils/theme.util';
import {
  InfoRow,
  RISK_BADGE_TONE_COLOR,
  RiskScoreBadge
} from './AiPanelBlock.styles';
import { getIconType, getIconVariant, getToneColor } from './aiPanelIcons';
import { AiPanelLinkView } from './AiPanelLinkView';
import BulletListBlock from './BulletListBlock';

export { AiPanelLinkView };

export const AiPanelBlockView: React.FC<{ block: AiPanelBlock }> = ({
  block
}) => {
  switch (block.type) {
    case AiPanelBlockType.Text:
      return (
        <Flex align="center" gap={semanticTheme.dime.spacing.s}>
          <Typography
            variant={block.bold ? 'body1Bold' : 'body1'}
            color={getToneColor(block.tone)}
          >
            {block.text}
          </Typography>
          {!!block.copyable && (
            <CopyToClipboard text={block.copyText ?? block.text} />
          )}
        </Flex>
      );

    case AiPanelBlockType.BulletList:
      return (
        <BulletListBlock
          items={block.items}
          maxItems={block.maxItems}
          charLimit={block.charLimit}
        />
      );

    case AiPanelBlockType.KeyValueRows:
      return (
        <Flex vertical gap={4}>
          {block.rows.map((row, i) => (
            <Flex key={i} align="center" gap={semanticTheme.dime.spacing.s}>
              <Typography variant="body1">
                <span
                  style={{ color: semanticTheme.dime.colors.text.secondary }}
                >
                  {row.label}{' '}
                </span>
                {row.valueLink ? (
                  <AiPanelLinkView link={row.valueLink} />
                ) : (
                  <span style={{ color: getToneColor(row.valueTone) }}>
                    {row.value}
                  </span>
                )}
              </Typography>
              {!!row.copyable && (
                <CopyToClipboard text={row.copyText ?? row.value} />
              )}
            </Flex>
          ))}
        </Flex>
      );

    case AiPanelBlockType.IconRows:
      return (
        <Flex vertical gap={semanticTheme.dime.spacing.s}>
          {block.rows.map((row, i) => (
            <InfoRow key={i}>
              {!!getIconType(row.icon) && (
                <IconWrap
                  type={getIconType(row.icon) as IconType}
                  size={IconSize.Small}
                  style={{ color: semanticTheme.dime.colors.text.secondary }}
                />
              )}
              {row.link ? (
                <AiPanelLinkView link={row.link} />
              ) : (
                <Typography variant="body1">{row.text}</Typography>
              )}
              {!!row.badge && (
                <RiskScoreBadge
                  bgColor={
                    row.badge.tone === 'emailAge'
                      ? theme.colors.emailAge.emailAgeScore(
                          Number(row.badge.text)
                        ).backgroundColor
                      : RISK_BADGE_TONE_COLOR[row.badge.tone] ??
                        semanticTheme.dime.colors.fill.successPrimary
                  }
                  color={
                    row.badge.tone === 'emailAge'
                      ? theme.colors.emailAge.emailAgeScore(
                          Number(row.badge.text)
                        ).color
                      : undefined
                  }
                >
                  {row.badge.text}
                </RiskScoreBadge>
              )}
              <CopyToClipboard text={row.copyText ?? row.text} space={4} />
              {
                <IconWrap
                  type={IconType.AiSparkle}
                  size={IconSize.Small}
                  color={semanticTheme.dime.colors.text.secondary}
                />
              }
            </InfoRow>
          ))}
        </Flex>
      );

    case AiPanelBlockType.Links:
      return (
        <Flex vertical gap={semanticTheme.dime.spacing.s}>
          {block.links.map((link, i) => (
            <AiPanelLinkView key={i} link={link} />
          ))}
        </Flex>
      );

    case AiPanelBlockType.Divider:
      return <Divider />;

    case AiPanelBlockType.Group:
      return (
        <Flex vertical gap={semanticTheme.dime.spacing.s}>
          {(!!block.title || !!getIconType(block.titleIcon)) && (
            <Flex align="center" gap={semanticTheme.dime.spacing.s}>
              {!!block.title && (
                <Typography variant="body1Bold">{block.title}</Typography>
              )}
              {!!getIconType(block.titleIcon) && (
                <IconWrap
                  type={getIconType(block.titleIcon) as IconType}
                  variant={getIconVariant(block.titleIcon)}
                  size={IconSize.Small}
                  color={getToneColor(block.titleIconTone)}
                />
              )}
            </Flex>
          )}
          {block.blocks.map((child, i) => (
            <AiPanelBlockView key={i} block={child} />
          ))}
        </Flex>
      );

    default:
      return null;
  }
};
